// One-screen "request a shift" modal: pick a date and hour, optionally a role
// and a note. Used by the floating quick-add button and the simple home.
import { useState, type FormEvent } from 'react'
import { api, ApiError } from '../api'
import { useI18n } from '../i18n'
import type { JobRole } from '../types'
import { todayYmd } from '../lib/dates'
import { Modal, Spinner, useToast } from './ui'
import { IconPlus } from './icons'

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const hh = (h: number) => `${String(h).padStart(2, '0')}:00`

export default function QuickBook({
  roles,
  onClose,
  onBooked,
}: {
  roles: JobRole[]
  onClose: () => void
  onBooked?: () => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const [date, setDate] = useState(todayYmd())
  const [hour, setHour] = useState(() => new Date().getHours())
  const [roleId, setRoleId] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.createBooking({ date, hour, role_id: roleId || null, note: note.trim() || undefined })
      toast.push(t('toast.booked'), 'ok')
      onBooked?.()
      onClose()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.bookErr'), 'error')
      setBusy(false)
    }
  }

  return (
    <Modal title={t('topbar.bookShift')} subtitle={t('quick.book.sub')} onClose={onClose}>
      <form className="auth-form" onSubmit={submit}>
        <div className="field">
          <label>{t('ot.date')}</label>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </div>
        <div className="field">
          <label>{t('quick.time')}</label>
          <select className="input" value={hour} onChange={(e) => setHour(Number(e.target.value))}>
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {hh(h)}–{hh(h + 1)}
              </option>
            ))}
          </select>
        </div>
        {roles.length > 0 && (
          <div className="field">
            <label>
              {t('roles.label')} <span className="muted">({t('common.optional')})</span>
            </label>
            <select className="input" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              <option value="">{t('roles.none')}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label>
            {t('quick.note')} <span className="muted">({t('common.optional')})</span>
          </label>
          <input
            className="input"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('quick.notePlaceholder')}
          />
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <Spinner /> : <><IconPlus width={18} height={18} /> {t('topbar.bookShift')}</>}
          </button>
        </div>
      </form>
    </Modal>
  )
}
